import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { later } from '@ember/runloop';
import { service } from '@ember/service';

import { hashSettled } from 'rsvp';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import BillingPopupConfirmComponent from 'webapp/components/billing/popup-confirm';
import BillingPopupForbiddenFeatureComponent from 'webapp/components/billing/popup-forbidden-feature';
import BillingPopupFreeTrialRequestComponent from 'webapp/components/billing/popup-free-trial-request';
import BillingPopupPlanUpgradeFeatureComponent from 'webapp/components/billing/popup-plan-upgrade-feature';
import BillingPopupPlanUpgradeTrialComponent from 'webapp/components/billing/popup-plan-upgrade-trial';
import BillingPopupPlanUpgradeOfferComponent from 'webapp/components/billing/promotions/growth-2024/popup-plan-upgrade-offer';
import SourcePopupRequestComponent from 'webapp/components/source/popup-request';
import UserPopupForbiddenActionComponent from 'webapp/components/user/popup-forbidden-action';

import type SourceType from 'webapp/models-custom/source-type';

import type { TrackingOriginNewConnectionInitiated } from 'webapp/services/model-manager/source-types';

export default class AvailableSourceTypeInfoRoute extends Route {
	@service('connect') declare connectService: Service.Connect;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	trackingOrigin: TrackingOriginNewConnectionInitiated = 'datamanager';

	override beforeModel() {
		const promises = {
			metrics: this.mmMetricsService.requestMetrics(),
		};

		return hashSettled(promises);
	}

	@action
	override willTransition(_transition: Transition) {
		this.trackingOrigin = 'datamanager';
	}

	override model(params: { source_key: string }) {
		let model;

		if (params.source_key) {
			model = this.mmSourceTypesService.findModelByKey(params.source_key);
		}

		return model;
	}

	override afterModel(model: SourceType) {
		if (!model) {
			this.navigationService.replaceRoute('datamanager.available');
			return;
		}

		later(() => {
			this.mmSourceTypesService.triggerNewConnectionInitiated(model, this.trackingOrigin);
		}, 100);

		const onConnect = (sourceType: SourceType) => {
			if (!sourceType.isConnectable) {
				// open request popup
				const popup = this.popupService.createModal(SourcePopupRequestComponent, {
					popupClass: 'popup-type-source-request',
				});
				popup.open({ sourceType }); // <PopupData.Source.Request>
				return;
			}

			const onConnectSuccess = this.connectService.getAfterConnectAction();
			const isTourOnboardingFlow = this.flowTourOnboardingService.isBannerShown;

			void sourceType
				.connectNew({ origin: 'datamanagerAvailable' })
				.then((result) => {
					if (result.isSuccess) {
						const source = result.source;
						this.navigationService
							.transitionToRoute('datamanager.connected')
							.then(() => {
								if (!isTourOnboardingFlow) {
									later(() => {
										const properSource = sourceType.isExcelGroup
											? this.mmSourceTypesService.findModelById(source.type)
											: sourceType;

										if (properSource) {
											onConnectSuccess(
												properSource,
												+source.id,
												result.metadata,
											);
										}
									}, 400);
								}
							});
					}
				})
				.finally(() => {
					if (isTourOnboardingFlow) {
						later(() => {
							this.flowTourOnboardingService.continueTour();
						}, 400);
					} else if (
						this.navigationService.activatedRoute.startsWith(
							'datamanager.connected.',
						) ||
						this.navigationService.activatedRoute.startsWith('datamanager.available.')
					) {
						this.eventManagerService.trigger(AppEvent.CONNECT_INITIALIZATION_FINISHED);

						later(() => {
							const limitationPopups = [
								UserPopupForbiddenActionComponent,
								BillingPopupFreeTrialRequestComponent,
								BillingPopupForbiddenFeatureComponent,
								BillingPopupPlanUpgradeOfferComponent,
								BillingPopupConfirmComponent,
								BillingPopupPlanUpgradeTrialComponent,
								BillingPopupPlanUpgradeFeatureComponent,
							];

							const redirectFunc = () => {
								this.navigationService.transitionToRoute('datamanager.available');
							};

							const activePopup = this.popupService.activePopup;
							const activePopupClass = activePopup?.componentInnerClass;

							/**
							 * Use type assertion to ensure limitationPopups.includes() is type-safe.
							 * Fixes TS error where `componentInnerClass` may be a broader (base) type.
							 * Also checks that `activePopup` is defined before invoking `.on`.
							 */
							if (
								activePopup &&
								activePopupClass &&
								limitationPopups.includes(
									activePopupClass as (typeof limitationPopups)[number],
								)
							) {
								activePopup.on(PopupEvent.CLOSED, this, () => {
									if (
										activePopupClass !==
											BillingPopupForbiddenFeatureComponent &&
										activePopupClass !== BillingPopupPlanUpgradeFeatureComponent
									) {
										redirectFunc();
									}
								});
							} else {
								redirectFunc();
							}
						}, 100);
					}
				});
		};

		onConnect(model);
	}
}
